import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import type { TextProps } from "@chakra-ui/react";
import { Text } from "@chakra-ui/react";

import { Tooltip } from "../ui/tooltip";

type TruncatedTextProps = {
  content: ReactNode;
  openDelay?: number;
  disabled?: boolean;
} & Omit<TextProps, "content">;

export const TruncatedText = ({
  content,
  openDelay = 100,
  disabled = false,
  ...props
}: TruncatedTextProps) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const truncated = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(truncated);
      }
    };

    // Delay the truncation check to ensure rendering is complete
    const timeoutId = setTimeout(checkTruncation, 200);

    window.addEventListener("resize", checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [content]);

  const textElement = (
    <Text
      ref={textRef}
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      {...props}
    >
      {content}
    </Text>
  );

  if (!isTruncated) {
    return textElement;
  }

  return (
    <Tooltip
      content={content}
      contentProps={{
        css: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "center",
          zIndex: 10000,
        },
      }}
      positioning={{ offset: { mainAxis: 3 }, placement: "bottom" }}
      openDelay={openDelay}
      closeDelay={100}
      showArrow
      disabled={disabled || !isTruncated}
      arrowStyle={{ left: 16 }}
      portalled
    >
      {textElement}
    </Tooltip>
  );
};
